import { motion } from 'framer-motion';

import { heroReveal } from '#/lib/hero-reveal';
import type { SanityRailsFormLanding } from '#/lib/sanity/queries/railsFormLanding';
import {
  getSectionButtonTheme,
  getSectionThemeAccent,
  getSectionThemeSurface,
  isDarkSectionTheme,
  normalizeSectionTheme,
} from '#/lib/section-theme';
import type { PageLayoutSharedProps } from '#/lib/types';
import { cn } from '#/lib/utils';
import { SectionMascot } from '#/components/BlobMascot';
import { Button } from '#/components/Button';
import { FormCustom } from '#/components/Form/Custom';
import { HomepageEmailForm } from '#/components/Form/HomepageEmail';
import { formatHeadline, Headline } from '#/components/Headline';
import {
  HeroBackgroundGraphic,
  RAILS_LANDING_HERO_BACKGROUND,
} from '#/components/HeroBackgroundGraphic';

export function RailsFormLanding(
  props: SanityRailsFormLanding & PageLayoutSharedProps,
) {
  const isDark = isDarkSectionTheme(props.theme);
  const buttonTheme = getSectionButtonTheme(props.theme);
  // Charcoal gets dark form controls; purple/white stay light (same as HeroSplit).
  const formTheme =
    normalizeSectionTheme(props.theme) === 'charcoal' ? 'dark' : 'light';
  const ctaMode = props.ctaMode ?? 'redirectForm';
  const isFirst = props.sectionIndex === 0;

  return (
    <div
      className={cn(
        'p-main relative isolate overflow-hidden',
        getSectionThemeSurface(props.theme),
        isFirst
          ? 'mt-[calc(var(--header-height)*-1)] pb-60 pt-[calc(var(--header-height)+5rem)] m:pb-100 m:pt-[calc(var(--header-height)+8rem)]'
          : 'pt-80 pb-60 m:pt-120 m:pb-100',
      )}
      // Paint the graphic's base colour before the image loads (and behind
      // overscroll) so the surface never flashes the page background.
      style={{ backgroundColor: RAILS_LANDING_HERO_BACKGROUND.baseColor }}
    >
      <HeroBackgroundGraphic background={RAILS_LANDING_HERO_BACKGROUND} />
      {/* Soften the graphic crop into the section surface */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-160 bg-gradient-to-t from-charcoal to-transparent"
      />
      <SectionMascot preset={props.mascot} />
      <div className="relative z-[1] mx-auto flex max-w-960 flex-col items-center text-center">
        <motion.div {...heroReveal(0)}>
          <Headline
            value={props.headline}
            variant={2}
            highlightClass={getSectionThemeAccent(props.theme)}
            className="text-balance"
          />
        </motion.div>
        <motion.p
          {...heroReveal(1)}
          className={cn(
            'type-heading5 m:type-heading4 mt-20 max-w-720 text-balance',
            isDark ? 'text-cloud' : 'text-charcoal',
          )}
        >
          {formatHeadline(props.bodyCopy, getSectionThemeAccent(props.theme))}
        </motion.p>

        {ctaMode === 'standardForm' && props.form ? (
          <motion.div
            {...heroReveal(2)}
            className="mt-40 flex w-full flex-col items-center gap-16 m:mt-48 m:flex-row m:items-start m:justify-center m:gap-24"
          >
            <div className="w-full max-w-[500px] text-left">
              <FormCustom
                className={cn(
                  isDark &&
                    '[&_.legal-text]:!text-cloud [&_.step-indicator]:!text-cloud',
                )}
                form={props.form}
                theme={formTheme}
                buttonTheme={buttonTheme}
                confirmation={props.formConfirmation ?? ''}
              />
            </div>
            {props.secondaryButton?.href ? (
              <Button
                variant="secondary"
                theme={formTheme}
                href={props.secondaryButton.href}
                target={props.secondaryButton.target}
                className="hidden h-50 items-center justify-center self-start text-center m:flex"
              >
                {props.secondaryButton.title}
              </Button>
            ) : null}
          </motion.div>
        ) : null}

        {ctaMode === 'redirectForm' &&
        (props.formFunnel === 'rails' || props.form?.formType === 'contact') ? (
          <motion.div
            {...heroReveal(2)}
            className="mt-40 flex w-full flex-col items-center gap-16 m:mt-48 m:flex-row m:items-start m:justify-center m:gap-24"
          >
            <HomepageEmailForm
              className="w-full max-w-[500px]"
              form={props.form?.formType === 'contact' ? props.form : null}
              formFunnel={props.formFunnel ?? 'rails'}
              primaryButtonLabel={
                props.primaryButtonLabel ?? 'Apply for Waitlist'
              }
              emailPlaceholder={props.emailPlaceholder ?? 'Work email'}
              theme={formTheme}
              legalText={props.legalText}
              legalTextClassName={cn('text-left', isDark && '!text-cloud')}
            />
            {props.formFunnel !== 'rails' && props.secondaryButton?.href ? (
              <Button
                variant="secondary"
                theme={formTheme}
                href={props.secondaryButton.href}
                target={props.secondaryButton.target}
                className="hidden h-50 items-center justify-center self-start text-center m:flex"
              >
                {props.secondaryButton.title}
              </Button>
            ) : null}
          </motion.div>
        ) : null}

        {props.trialText ? (
          <motion.p
            {...heroReveal(3)}
            className={cn(
              'mt-28 max-w-640 text-16 text-balance',
              isDark ? 'text-cloud' : undefined,
            )}
          >
            {props.trialText}
          </motion.p>
        ) : null}
      </div>
    </div>
  );
}
